// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// Shared view chrome. Every view opens with the question it answers, because
// a chart nobody can name a question for should not be on the page.

import { esc, tipAttr } from '../format';
import type { Finding } from '../analysis';

export function viewHead(title: string, question: string, blurb: string): string {
  return `<div class="view-head">
    <h1>${title}</h1>
    <p class="view-q">${question}</p>
    <p class="view-blurb">${blurb}</p>
  </div>`;
}

export function card(title: string, sub: string, body: string, extraClass = ''): string {
  return `<section class="card ${extraClass}">
    <div class="card-head"><h2>${title}</h2><p>${sub}</p></div>
    ${body}
  </section>`;
}

export interface Stat {
  value: string;
  label: string;
  sub?: string;
  tone?: 'good' | 'warn' | 'bad' | 'psych' | 'fatal';
  tip?: string;
}

export function stats(items: Stat[]): string {
  return `<div class="stat-row">${items
    .map(
      (s) => `<div class="stat${s.tone ? ` stat-${s.tone}` : ''}"${s.tip ? ` data-tip="${tipAttr(s.tip)}"` : ''}>
        <span class="stat-v">${esc(s.value)}</span>
        <span class="stat-l">${s.label}</span>
        ${s.sub ? `<span class="stat-s">${s.sub}</span>` : ''}
      </div>`,
    )
    .join('')}</div>`;
}

export function findingCards(items: Finding[]): string {
  if (!items.length) return `<p class="empty">No findings met the detection thresholds for this release.</p>`;
  return `<div class="findings">${items
    .map(
      (f) => `<article class="finding finding-${f.severity}">
        <h3>${esc(f.title)}</h3>
        <p>${esc(f.body)}</p>
        <p class="finding-rule"><strong>How this was detected:</strong> ${esc(f.rule)}</p>
        ${f.view ? `<a href="#/${f.view}">Open the ${esc(f.view)} view →</a>` : ''}
      </article>`,
    )
    .join('')}</div>`;
}

export function legend(items: { color: string; label: string }[], live = false): string {
  return `<div class="legend${live ? ' legend-live' : ''}">${items
    .map((i) => `<span class="legend-item"><span class="swatch" style="background:${i.color}"></span>${esc(i.label)}</span>`)
    .join('')}</div>`;
}

export function segmented(name: string, options: { value: string; label: string }[], active: string): string {
  return `<div class="seg" role="group" aria-label="${esc(name)}">${options
    .map(
      (o) => `<button type="button" data-seg="${esc(name)}" data-value="${esc(o.value)}" class="${o.value === active ? 'active' : ''}" aria-pressed="${o.value === active}">${esc(o.label)}</button>`,
    )
    .join('')}</div>`;
}

export function note(body: string, tone: 'info' | 'warn' = 'info'): string {
  return `<p class="note-band note-${tone}">${body}</p>`;
}

/** A wide-by-nature chart scrolls inside its own box; the page never does. */
export function chartBox(inner: string, width: 'normal' | 'wide' | 'xwide' = 'normal'): string {
  const cls = width === 'normal' ? 'chart' : width === 'wide' ? 'chart chart-wide' : 'chart chart-xwide';
  return `<div class="${cls}">${inner}</div>`;
}

/**
 * Attributes that make a whole table row behave like a button for keyboard and
 * screen-reader users. A `cursor: pointer` row with only a click handler is
 * invisible to everyone not using a mouse.
 */
export function rowActivationAttrs(label: string): string {
  return ` tabindex="0" role="button" aria-label="${esc(label)}"`;
}

export function sortableTable(opts: {
  columns: { key: string; label: string; num?: boolean; wide?: boolean }[];
  rows: { cells: string[]; click?: string; tip?: string }[];
  sortKey: string;
  sortDir: 'asc' | 'desc';
  minWidth?: number;
}): string {
  return `<div class="tbl-wrap"><table class="data"${opts.minWidth ? ` style="min-width:${opts.minWidth}px"` : ''}>
    <thead><tr>${opts.columns
      .map(
        (c) => `<th scope="col" class="sortable${c.num ? ' num' : ''}"${c.key === opts.sortKey ? ` aria-sort="${opts.sortDir === 'asc' ? 'ascending' : 'descending'}"` : ''}>
          <button type="button" class="th-sort" data-sort="${esc(c.key)}">${esc(c.label)}${c.key === opts.sortKey ? (opts.sortDir === 'asc' ? ' ▲' : ' ▼') : ''}</button></th>`,
      )
      .join('')}</tr></thead>
    <tbody>${opts.rows
      .map(
        (r) => `<tr class="${r.click ? 'clickable' : ''}"${r.click ?? ''}${r.tip ? ` data-tip="${tipAttr(r.tip)}"` : ''}>${r.cells
          .map((cell, i) => `<td class="${opts.columns[i]?.num ? 'num' : ''}${opts.columns[i]?.wide ? ' wide' : ''}">${cell}</td>`)
          .join('')}</tr>`,
      )
      .join('')}</tbody></table></div>`;
}
